import { memo } from 'react';

import { AtomIcon } from '@air/design-icons';

import {
  MainSideNavItem,
  type MainSideNavSidebarInteractionType,
} from '~/components/v2/components/workspace/main-side-nav/item/MainSideNavItem';
import { useMainSideNavItemSelected } from '~/components/v2/components/workspace/main-side-nav/useMainSideNavItemSelected';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { usePrivateContextPeopleTabs } from '~/components/v2/hooks/usePrivateContextPeopleTabs';

export type MainSideNavItemContextProps = {
  pathname: string;
};

// Same fallback order the tab bar itself uses: Brand & content, then People, then Sizes.
const getContextHref = ({
  canSeeBrandContentTab,
  canSeePeopleTab,
}: {
  canSeeBrandContentTab: boolean;
  canSeePeopleTab: boolean;
}): Pathnames => {
  if (canSeeBrandContentTab) {
    return Pathnames.ContextSources;
  }

  if (canSeePeopleTab) {
    return Pathnames.People;
  }

  return Pathnames.ContextSizes;
};

const getContextSidebarInteractionType = (href: Pathnames): MainSideNavSidebarInteractionType | undefined => {
  if (href === Pathnames.People) {
    return 'people';
  }

  if (href === Pathnames.ContextSources) {
    return 'context_sources';
  }

  return undefined;
};

export const MainSideNavItemContext = memo(({ pathname }: MainSideNavItemContextProps) => {
  const { canSeeBrandContentTab, canSeePeopleTab, canSeeSavedSettingsTab } = usePrivateContextPeopleTabs();
  const canSeeContextItem = canSeeBrandContentTab || canSeePeopleTab || canSeeSavedSettingsTab;
  const { isSelected } = useMainSideNavItemSelected({
    isRouteSelected:
      pathname === Pathnames.ContextSources ||
      pathname === Pathnames.ContextSourcesByContextSourceId ||
      (canSeePeopleTab && pathname === Pathnames.People) ||
      (canSeeSavedSettingsTab && pathname === Pathnames.ContextSizes),
  });

  if (!canSeeContextItem) {
    return null;
  }

  const contextHref = getContextHref({ canSeeBrandContentTab, canSeePeopleTab });
  const sidebarInteractionType = getContextSidebarInteractionType(contextHref);

  return (
    <MainSideNavItem
      href={contextHref}
      icon={AtomIcon}
      id="context"
      isSelected={isSelected}
      label="Context"
      sidebarInteractionType={sidebarInteractionType}
    />
  );
});

MainSideNavItemContext.displayName = 'MainSideNavItemContext';
